import React from 'react';
import ReactDOM, {Root} from 'react-dom/client';
import _debug from "debug";

import App from './ui/app';
import reportWebVitals from './reportWebVitals';

import './scss/styles.scss';
import {
  getAuthzUrl,
  getClientId,
  getJwksUrl,
  getLogoutPath,
  getSloUrl,
  getTokenUrl
} from "./api/ApiImplementation";
import {getEnvParam, REACT_APP_AUTHZ_API_IMPL, REACT_APP_BASE} from "./util/env";
import AppGatekeeperAuthenticator from "./auth/AppGatekeeperAuthenticator";
import {LocaleManager} from "./util/locale";
import store, {getAuthentication} from "./redux/store";
import {initializeApis} from "./api";
import {Provider} from "react-redux";
import AuthenticationUtils from "./redux/util/authenticationUtils";
import LocalizeContext from "./ui/localize/localize-context";
import Localize from "./ui/localize";
import {addBsThemeScssSupport} from "./util/bs-theme-scss-support";
import XWindowEvents from "./util/x-window-events";
import {InitialUIConfiguration} from "./ui-configuration/configuration-provider";

const debug = _debug("App:bootstrap");

const currentUrl = new URL(window.location.href);

const idpAuthzUrl = getAuthzUrl();
debug("authz url: %s", idpAuthzUrl.toString());
const idpTokenUrl = getTokenUrl();
debug("token url: %s", idpTokenUrl.toString());
const idpSloUrl = getSloUrl();
debug("single logout url: %s", idpSloUrl.toString());
const idpJwksUrl = getJwksUrl();
debug("JWKS url: %s", idpJwksUrl.toString());
const clientId = getClientId();
debug("client_id: %s", clientId);
const reactAppBase = getEnvParam(REACT_APP_BASE, "/");
debug("appBase: %s", reactAppBase);
const authnResponseUrl = new URL(
    `${currentUrl.protocol}//${currentUrl.host}${reactAppBase}`
);

const authzApi = getEnvParam(REACT_APP_AUTHZ_API_IMPL, "internalMock");

debug("callback_uri: %s", authnResponseUrl.toString());
const logoutPath = getLogoutPath();
debug("local logout route: %s", logoutPath);
const idTokenIatLeeway: number = InitialUIConfiguration.security
    ?.idTokenIatLeeway as number;
debug("ID token iat leeway (seconds): %d", idTokenIatLeeway);

const authenticator = new AppGatekeeperAuthenticator(
    idpAuthzUrl,
    idpTokenUrl,
    idpSloUrl,
    clientId,
    idpJwksUrl,
    authnResponseUrl,
    logoutPath,
    authzApi !== 'real',
    idTokenIatLeeway
);


/**
 * Subscribe to store changes and detect auth changes & errors
 */
const unsubcribe = store.subscribe(() => {
  let root: Root;
  let auth = getAuthentication();
  // let externalLogout: boolean | undefined;
  // check if Authentication is complete
  if (AuthenticationUtils.selectAuthCompleted(store.getState())) {
    // unsubscribe this handler to avoid this handler being hit again after the first time
    unsubcribe();

    let externalLogout: boolean | undefined;

    XWindowEvents.subscribeLogout((event) => {
      console.log('wtf %o', auth);
      if (auth) {
        externalLogout = true;
        renderApp();
      }
    });

    /**
     * Initializes the APIs, if possible. Api init requires an auth state, reject if no auth.
     */
    const apiInit = () => {
      return new Promise<void>((resolve, reject) => {
        if (auth) {
          initializeApis();
          resolve();
        } else {
          reject();
        }
      });
    };
    const preRender = () => {
      const container = document.getElementById("root");
      root = ReactDOM.createRoot(container!);
    };
    /**
     * Renders the primary app once all the initialization steps have been successfully passed.
     */
    const renderApp = () => {
      root?.render(<React.StrictMode>
        <Localize>
          <Provider store={store}>
            <LocalizeContext.Consumer>
              {(context) => (
                  <App
                      localeInPath={context.path || ""}
                      externalLogout={externalLogout}
                  />
              )}
            </LocalizeContext.Consumer>
          </Provider>
        </Localize>
      </React.StrictMode>);
    };
    preRender();
    apiInit()
        .then(renderApp)
        .catch(renderApp)
    ;
  }
});

// triggers the authentication process
authenticator.handleAuthentication().then((r) => {
  if (!!r) {
    // The authentication is completed with a non event firing window.location rewrite,
    // so we need to trigger locale resolver.
    LocaleManager.updateState();
  }
});

document.addEventListener("DOMContentLoaded", function(event) {
  const tags = window.document.getElementsByTagName('html');
  if (tags && tags.length) {
    addBsThemeScssSupport(tags[0]);
  }
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);
